// Multi-device write conflicts.
//
// When an offline edit is replayed we compare the fields it touches against the
// baseline the device had when the edit was made. If another device changed one
// of those fields in the meantime, the write is NOT applied blindly: it is
// parked here as a conflict so the doctor can pick which version wins.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";

const DB_NAME = "doctorsx-sync-conflicts";
const DB_VERSION = 1;
const STORE = "conflicts";

/** Tables where a two-device edit needs an explicit human decision. */
export const CONFLICT_TABLES = new Set(["appointments", "invoices"]);

export interface ConflictField {
  field: string;
  /** value the device had when the edit was made */
  base: unknown;
  /** value this device wants to write */
  mine: unknown;
  /** value currently on the server (written by another device) */
  theirs: unknown;
}

export interface SyncConflict {
  key: string;
  table: string;
  rowId: string;
  label?: string;
  /** full payload of the local edit */
  payload: Record<string, unknown>;
  /** full server row at detection time */
  serverRow: Record<string, unknown>;
  fields: ConflictField[];
  detected_at: number;
  queued_at: number;
}

interface ConflictSchema extends DBSchema {
  conflicts: { key: string; value: SyncConflict };
}

let dbPromise: Promise<IDBPDatabase<ConflictSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<ConflictSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* ------------------------------------------------------------------ */
/* Subscriptions                                                       */
/* ------------------------------------------------------------------ */

type Listener = (items: SyncConflict[]) => void;
const listeners = new Set<Listener>();
const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("doctorsx-sync-conflicts")
    : null;

export async function listConflicts(): Promise<SyncConflict[]> {
  const db = await getDb();
  const all = await db.getAll(STORE);
  return all.sort((a, b) => a.detected_at - b.detected_at);
}

async function notify() {
  const items = await listConflicts();
  listeners.forEach((l) => l(items));
}

async function emit() {
  await notify();
  channel?.postMessage({ type: "changed" });
}

channel?.addEventListener("message", () => void notify());

export function subscribeConflicts(fn: Listener): () => void {
  listeners.add(fn);
  void listConflicts().then(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* ------------------------------------------------------------------ */
/* Detection                                                           */
/* ------------------------------------------------------------------ */

const IGNORED_FIELDS = new Set(["updated_at", "created_at", "user_id", "id"]);

function sameValue(a: unknown, b: unknown) {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === "object" || typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return String(a) === String(b);
}

/**
 * Compare the local edit against the current server row.
 * Returns the conflicting fields, or an empty array when the write is safe.
 */
export function diffConflict(
  payload: Record<string, unknown>,
  baseline: Record<string, unknown> | undefined,
  serverRow: Record<string, unknown>,
): ConflictField[] {
  if (!baseline) return [];
  const out: ConflictField[] = [];
  for (const [field, mine] of Object.entries(payload)) {
    if (IGNORED_FIELDS.has(field)) continue;
    const base = baseline[field];
    const theirs = serverRow[field];
    // Another device changed this field since we read it, and our value differs.
    if (!sameValue(base, theirs) && !sameValue(mine, theirs)) {
      out.push({ field, base, mine, theirs });
    }
  }
  return out;
}

export async function recordConflict(entry: {
  table: string;
  rowId: string;
  label?: string;
  payload: Record<string, unknown>;
  serverRow: Record<string, unknown>;
  fields: ConflictField[];
  queued_at?: number;
}) {
  const db = await getDb();
  const existing = (await db.getAll(STORE)).find(
    (c) => c.table === entry.table && c.rowId === entry.rowId,
  );
  const key = existing?.key ?? uuid();
  await db.put(STORE, {
    key,
    table: entry.table,
    rowId: entry.rowId,
    label: entry.label,
    payload: entry.payload,
    serverRow: entry.serverRow,
    fields: entry.fields,
    detected_at: Date.now(),
    queued_at: entry.queued_at ?? Date.now(),
  });
  await emit();
  return key;
}

export async function countConflicts() {
  const db = await getDb();
  return db.count(STORE);
}

export async function dismissConflict(key: string) {
  const db = await getDb();
  await db.delete(STORE, key);
  await emit();
}

/* ------------------------------------------------------------------ */
/* Resolution                                                          */
/* ------------------------------------------------------------------ */

export type ResolutionChoice = "mine" | "theirs";

/**
 * Apply a resolution. `choices` maps field -> winner; fields left out default
 * to "theirs" (server value kept). Only fields resolved to "mine" are written.
 */
export async function resolveConflict(
  key: string,
  choices: Record<string, ResolutionChoice>,
) {
  const db = await getDb();
  const conflict = await db.get(STORE, key);
  if (!conflict) return;

  const updates: Record<string, unknown> = {};
  // Non-conflicting fields from the local edit still need to land.
  const conflicting = new Set(conflict.fields.map((f) => f.field));
  for (const [field, value] of Object.entries(conflict.payload)) {
    if (IGNORED_FIELDS.has(field)) continue;
    if (!conflicting.has(field)) updates[field] = value;
  }
  for (const f of conflict.fields) {
    if (choices[f.field] === "mine") updates[f.field] = f.mine;
  }

  if (Object.keys(updates).length > 0) {
    const { error } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from(conflict.table as any)
      .update(updates)
      .eq("id", conflict.rowId);
    if (error) throw error;
  }

  await db.delete(STORE, key);
  await emit();
}

/** Keep every local value (last-write-wins in favour of this device). */
export async function keepMine(key: string) {
  const db = await getDb();
  const conflict = await db.get(STORE, key);
  if (!conflict) return;
  const choices: Record<string, ResolutionChoice> = {};
  conflict.fields.forEach((f) => {
    choices[f.field] = "mine";
  });
  await resolveConflict(key, choices);
}

/** Discard the local edit entirely and keep what the other device saved. */
export async function keepTheirs(key: string) {
  await resolveConflict(key, {});
}
